import React, { useState, useEffect } from "react";
import { OverlayTrigger, Tooltip, Card } from "react-bootstrap";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import closureApi from "../../api/closureApi";
import { ResponsiveDataList } from "../../components/responsive";
import moment from "moment-timezone";
import Helpers from "../../components/Helpers";
import AlertDelete from "../../components/AlertDelete";
import MailRecipientsModal from "./MailRecipientsModal";
import { ClosureRetreatCard } from "./manage/cards/ClosureRetreatCard";
import { ClosureListToolbar } from "./manage/ClosureListToolbar";
import { ClosureStatusButtons } from "./manage/ClosureStatusButtons";
import {
  closureRetreatTitle,
  formatClosureDateRange,
  isUpcomingClosure,
} from "./manage/closureDisplay";

const ClosuresList: React.FC = () => {
  let navigate = useNavigate();
  const location = useLocation();
  const ClosureApi = new closureApi();
  const renderTooltip = (props: any) => (
    <Tooltip id="button-tooltip">{props}</Tooltip>
  );

  const [allRetreatClosures, setAllRetreatClosures] = useState<any[]>([]);
  const [searchKey, setSearchKey] = useState<string>(
    () => (location.state as any)?.searchKeyword || "",
  );
  const [LoadData, setLoadData] = useState<boolean>(false);
  const [total, setTotal] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(20);
  const [selectedRetreats, setSelectedRetreats] = useState<any[]>([]);
  const [userData, setUserData] = useState<Record<string, any> | null>(null);
  const [showingRetreats, setShowingRetreats] = useState<boolean>(false);
  const [locationData, setLocationData] = useState<Record<string, any>>({
    state: { parishValue: null },
  });
  const [confirmedData, setConfirmedData] = useState<boolean>(false);
  const [retreatDetail, setRetreatDetail] = useState<any>([]);
  const [leadMail, setLeadMail] = useState<boolean>(false);
  const [mailMessage, setMailMessage] = useState<string>("");
  const [dateFilter, setDateFilter] = useState<string>("all");
  const [dateFrom, setDateFrom] = useState<any>(null);
  const [dateTo, setDateTo] = useState<any>(null);

  const buildMailMessage = (row: any) => {
    const v = row.validationSummary || {};
    const retreatName = closureRetreatTitle(row);

    const statusLine = (label, info, showReason = true) => {
      const icon = info?.valid ? "✅" : "❌";
      const why =
        !info?.valid && showReason
          ? `<br/><em style="color:#dc3545;">Why? ${info?.message || "—"}</em>`
          : "";

      return `<p style="margin:4px 0;">
      <strong>${icon} ${label}:</strong> 
      ${info?.valid ? "Confirmed" : "Not Confirmed"}
      ${why}
    </p>`;
    };

    const missingCouples = v.rosterData?.missingCouples || [];
    const missingCouplesHtml =
      missingCouples.length > 0
        ? `<hr/>
         <h4 style="margin:8px 0 4px;">Couples with Missing Fields</h4>
         <table border="1" cellpadding="6" cellspacing="0"
                style="border-collapse:collapse;width:100%;font-size:13px;">
           <thead style="background:#f0f0f0;">
             <tr>
               <th>Couple ID</th>
               <th>Missing Fields</th>
             </tr>
           </thead>
           <tbody>
             ${missingCouples
               .map(
                 (c) => `<tr>
                 <td>${c.coupleId ?? "—"}</td>
                 <td>${
                   Array.isArray(c.fields)
                     ? c.fields.join(", ")
                     : (c.fields ?? "—")
                 }</td>
               </tr>`,
               )
               .join("")}
           </tbody>
         </table>`
        : "";

    const isClosedValid =
      row.retreatCloseStatus === false || row.retreatCloseStatus === 1;

    return `
<p style="margin:0 0 2px 0; font-size:18px; font-weight:700; color:#000;">
   Retreat Name:  ${retreatName || "—"}
</p>
<p style="margin:0 0 12px 0; font-size:17px; font-weight:700; color:#000;">
  Retreat ID: ${row.id}
</p>

<h3 style="margin-bottom:8px;">Retreat Confirmation Status</h3>
<p style="margin-bottom:12px;color:#555;">
  Please review the confirmation status for the retreat listed below and
  complete any outstanding items at your earliest convenience.
</p>
${statusLine("Roster Data", v.rosterData)}
${statusLine("Directory", v.directory, false)}
${statusLine("Program", v.program, false)}
${statusLine("Pictures", v.pictures)}
<p style="margin:4px 0;">
  <strong>${isClosedValid ? "✅" : "❌"} Validated &amp; Closed:</strong>
  ${isClosedValid ? "Retreat data confirmed." : "Retreat data not confirmed."}
</p>
${missingCouplesHtml}
<br/>`;
  };

  const columns = [
    {
      id: "retreatId",
      name: "RetreatId",
      selector: (row) => row?.id,
      sortable: true,
      reorder: true,
      width: "110px",
      style: {
        cursor: "text",
      },
    },
    {
      id: "title",
      name: "Retreat Title",
      selector: (row) => {
        const displayDate = formatClosureDateRange(row.dateFrom, row.dateTo);

        return (
          <>
            <a
              href={`/retreatRoster?retreatId=${row.id}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <div>{closureRetreatTitle(row)}</div>
            </a>

            {displayDate && (
              <OverlayTrigger
                placement="bottom"
                delay={{ show: 250, hide: 400 }}
                overlay={renderTooltip(displayDate)}
              >
                <span>{displayDate}</span>
              </OverlayTrigger>
            )}
          </>
        );
      },
      sortable: true,
      reorder: true,
      width: "300px",
      conditionalCellStyles: [
        {
          when: (row) => isUpcomingClosure(row),
          style: {
            backgroundColor: "rgb(196 240 196)",
          },
        },
      ],
    },
    {
      id: "confirmations",
      name: "Confirmations",
      width: "500px",
      cell: (row) => <ClosureStatusButtons row={row} />,
    },
    {
      id: "Send Email",
      name: "Send Email",
      cell: (row) => (
        <OverlayTrigger
          placement="bottom"
          delay={{ show: 250, hide: 400 }}
          overlay={renderTooltip(
            "Send Email to Lead Couples and Steering Committee ",
          )}
        >
          <span
            onClick={() => {
              setLeadMail(true);
              setRetreatDetail(row);
              setMailMessage(buildMailMessage(row));
            }}
          >
            <i
              className="mdi mdi-email-outline font-size action-icons"
              title="Send Email"
              style={{ fontSize: "20px" }}
            ></i>
          </span>
        </OverlayTrigger>
      ),
      reorder: true,
      sortable: true,
      width: "130px",
      style: {
        cursor: "pointer",
      },
      conditionalCellStyles: [
        {
          when: (row) => isUpcomingClosure(row),
          style: {
            backgroundColor: "rgb(196 240 196)",
          },
        },
      ],
    },
  ];

  useEffect(() => {
    getRetreatClosures();
  }, []);

  const handleSearch = (e) => {
    setSearchKey(e.target.value);
    getRetreatClosures(1, pageSize, e.target.value);
  };

  const handleFilterChange = (filter) => {
    setDateFilter(filter);

    if (filter !== "custom") {
      setDateFrom(null);
      setDateTo(null);
    }

    applyDateFilter(filter, null, null);
  };

  const handleDateFromChange = (date) => {
    setDateFrom(date);
    if (date) {
      setDateFilter("custom");
      applyDateFilter("custom", date, dateTo);
    } else if (!dateTo) {
      setDateFilter("all");
      applyDateFilter("all", null, null);
    }
  };

  const handleDateToChange = (date) => {
    setDateTo(date);
    if (date) {
      setDateFilter("custom");
      applyDateFilter("custom", dateFrom, date);
    } else if (!dateFrom) {
      setDateFilter("all");
      applyDateFilter("all", null, null);
    }
  };

  const applyDateFilter = (filter, customDateFrom, customDateTo) => {
    const filterData = {
      filterType: filter,
      dateFrom: customDateFrom
        ? moment(customDateFrom).format("YYYY-MM-DD")
        : null,
      dateTo: customDateTo ? moment(customDateTo).format("YYYY-MM-DD") : null,
    };

    getRetreatClosures(
      1,
      pageSize,
      searchKey,
      {},
      false,
      null,
      "",
      null,
      filterData,
    );
  };

  const clearFilters = () => {
    setDateFilter("all");
    setDateFrom(null);
    setDateTo(null);
    setSearchKey("");
    getRetreatClosures(1, pageSize, "", {}, false, null, "", null, {
      filterType: "all",
      dateFrom: null,
      dateTo: null,
    });
  };

  const getRetreatClosures = async (
    pageNo = 1,
    size = 20,
    searchKeyword = null,
    sortBy = {},
    loadCount = false,
    coupleId = null,
    committeeId = "",
    parishValue = null,
    dateFilterData = null,
  ) => {
    setAllRetreatClosures([]);
    setLoadData(true);
    let d: any = {};

    if (pageNo && size) {
      d.pageNo = pageNo;
      d.size = size;
    }

    if (searchKeyword !== null) {
      d.searchKeyword = searchKeyword;
    } else {
      d.searchKeyword = searchKey;
    }

    if ((sortBy as any).sortColumn) {
      d.sortBy = sortBy;
    }

    d.export = false;
    d.coupleId = coupleId;
    d.committeeId = committeeId;
    d.parishValue = parishValue || null;

    if (dateFilterData) {
      d.dateFilter = dateFilterData;
    } else if (dateFilter !== "all" || dateFrom || dateTo) {
      d.dateFilter = {
        filterType: dateFilter,
        dateFrom: dateFrom ? moment(dateFrom).format("YYYY-MM-DD") : null,
        dateTo: dateTo ? moment(dateTo).format("YYYY-MM-DD") : null,
      };
    }

    if (userData?.committeeMember === true && userData?.userRole !== "ADMIN") {
      d.committeeId = userData.id;
    }

    await ClosureApi.getAllRetreatClosures(d)
      .then((res) => {
        if (res.success) {
          let totalRecords = res.totalRecords ? res.totalRecords : 0;
          setTotal(totalRecords);

          const parsedData = res.data.map((item) => {
            const { confirmation, schedule, ...rest } = item;
            const parsedSchedule = schedule ? JSON.parse(schedule) : [];
            const parsedConfirmation = confirmation
              ? JSON.parse(confirmation)
              : [];
            return {
              ...rest,
              confirmation: parsedConfirmation,
              schedule: parsedSchedule.map((entry) => ({
                date: new Date(entry.date),
                from: entry.from,
                to: entry.to,
              })),
            };
          });

          setLoadData(false);

          if (parishValue && parishValue !== null) {
            const retreatIds = parsedData.map((retreat) => retreat.id);
            setSelectedRetreats(retreatIds);
          }

          setAllRetreatClosures(parsedData);
        } else {
          setLoadData(false);
          toast.error(res.msg);
        }
      })
      .catch((error) => {
        setLoadData(false);
        toast.error(error.message || "An error occurred");
      });
  };

  const getActiveFilterLabel = () => {
    if (dateFilter === "current")
      return `Current Month (${moment().format("MMMM YYYY")})`;
    if (dateFilter === "previous")
      return `Previous Month (${moment()
        .subtract(1, "month")
        .format("MMMM YYYY")})`;
    if (dateFilter === "custom" && (dateFrom || dateTo)) {
      const from = dateFrom ? moment(dateFrom).format("MMM D, YYYY") : "Start";
      const to = dateTo ? moment(dateTo).format("MMM D, YYYY") : "End";
      return `${from} - ${to}`;
    }
    return "";
  };

  const renderDatePickerHeader = ({
    date,
    changeYear,
    changeMonth,
    decreaseMonth,
    increaseMonth,
    prevMonthButtonDisabled,
    nextMonthButtonDisabled,
  }) => {
    const years = Array.from(
      { length: 100 },
      (_, i) => new Date().getFullYear() - i,
    );
    const months = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];

    return (
      <div style={{ textAlign: "center", paddingBottom: "8px" }}>
        <div
          style={{
            marginBottom: "10px",
            display: "flex",
            gap: "10px",
            justifyContent: "center",
          }}
        >
          <select
            style={{
              fontSize: "14px",
              padding: "4px 8px",
              borderRadius: "6px",
              border: "1px solid #ccc",
            }}
            value={months[date.getMonth()]}
            onChange={({ target: { value } }) =>
              changeMonth(months.indexOf(value))
            }
          >
            {months.map((month) => (
              <option key={month} value={month}>
                {month}
              </option>
            ))}
          </select>

          <select
            style={{
              fontSize: "14px",
              padding: "4px 8px",
              borderRadius: "6px",
              border: "1px solid #ccc",
            }}
            value={date.getFullYear()}
            onChange={({ target: { value } }) => changeYear(value)}
          >
            {years.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <button
            onClick={decreaseMonth}
            disabled={prevMonthButtonDisabled}
            style={{
              fontSize: "16px",
              padding: "2px 8px",
              cursor: "pointer",
              border: "none",
              background: "transparent",
            }}
          >
            {"<"}
          </button>

          <span style={{ fontWeight: "bold", fontSize: "14px" }}>
            {months[date.getMonth()]} {date.getFullYear()}
          </span>

          <button
            onClick={increaseMonth}
            disabled={nextMonthButtonDisabled}
            style={{
              fontSize: "16px",
              padding: "2px 8px",
              cursor: "pointer",
              border: "none",
              background: "transparent",
            }}
          >
            {">"}
          </button>
        </div>
      </div>
    );
  };

  const handleCloseAlert = () => {
    setConfirmedData(false);
    setRetreatDetail([]);
  };

  const sendMailToLead = async (
    selectedIds = [],
    messageContent = "",
    additionalEmails = [],
  ) => {
    if (
      !messageContent ||
      messageContent
        .replace(/<[^>]*>/g, "")
        .replace(/&nbsp;/g, "")
        .trim() === ""
    ) {
      toast.error("Please enter a message");
      return;
    }

    await ClosureApi.sendMailToLead({
      retreatId: retreatDetail.id,
      message: messageContent,
      recipientIds: selectedIds,
      additionalEmails: additionalEmails,
    })
      .then((res) => {
        if (res.success) {
          handleCloseMail();
          toast.success(res.msg);
        } else {
          toast.error(res.msg);
        }
      })
      .catch((error) => {
        toast.error(error.message || "An error occurred");
      });
  };

  const handleCloseMail = () => {
    setLeadMail(false);
    setRetreatDetail([]);
    setMailMessage("Test Content");
  };

  const fetchRecipients = async (retreatId) => {
    const res = await ClosureApi.getMailRecipients({ retreatId: retreatId });
    if (!res.success) throw new Error(res.msg);
    return res.data;
  };

  return (
    <div className="row ext-space">
      <div className="col-12">
        <Card className="mb-3 shadow-sm border-0">
          <Card.Body className="p-3">
            <ClosureListToolbar
              dateFilter={dateFilter}
              dateFrom={dateFrom}
              dateTo={dateTo}
              searchKey={searchKey}
              activeFilterLabel={getActiveFilterLabel()}
              onFilterChange={handleFilterChange}
              onDateFromChange={handleDateFromChange}
              onDateToChange={handleDateToChange}
              onSearch={handleSearch}
              onClear={clearFilters}
              renderDatePickerHeader={renderDatePickerHeader}
            />
          </Card.Body>
        </Card>

        <Card>
          <Card.Body>
            <ResponsiveDataList
              loading={LoadData}
              data={allRetreatClosures}
              countPerPage={pageSize}
              isServerSide
              pagination
              sortServer
              total={total}
              columns={columns}
              dataFetchApi={(
                pageNo,
                size,
                _search,
                sortBy,
                _sel,
                _committee,
                committeeId,
                parishValue,
              ) =>
                getRetreatClosures(
                  pageNo,
                  size,
                  searchKey || null,
                  sortBy || {},
                  false,
                  null,
                  committeeId ?? (showingRetreats ? userData?.id : ""),
                  parishValue ?? locationData?.state?.parishValue,
                  {
                    filterType: dateFilter,
                    dateFrom: dateFrom
                      ? moment(dateFrom).format("YYYY-MM-DD")
                      : null,
                    dateTo: dateTo
                      ? moment(dateTo).format("YYYY-MM-DD")
                      : null,
                  },
                )
              }
              pointerOnHover
              highlightOnHover
              committeeId={showingRetreats ? userData?.id : null}
              parishValue={locationData?.state?.parishValue}
              cardGridColumns={{ xs: "1fr", md: "repeat(2, minmax(0, 1fr))" }}
              renderCard={(row) => (
                <ClosureRetreatCard
                  row={row}
                  onSendEmail={(r) => {
                    setLeadMail(true);
                    setRetreatDetail(r);
                    setMailMessage(buildMailMessage(r));
                  }}
                />
              )}
            />
          </Card.Body>
        </Card>
      </div>

      {/* Confirmed Data Modal */}
      {confirmedData && (
        <AlertDelete
          show={confirmedData}
          alertHeader="Confirmed By"
          alerMessage={
            retreatDetail.confirmation &&
            retreatDetail.confirmation.length > 0 ? (
              <div>
                <a
                  href=""
                  onClick={(e) => {
                    e.preventDefault();
                    navigate(`/retreatRoster?retreatId=${retreatDetail.id}`);
                  }}
                >
                  <div>{retreatDetail.title}</div>
                </a>
                {retreatDetail.dateFrom === retreatDetail.dateTo ? (
                  <span>
                    {moment(retreatDetail && retreatDetail.dateFrom)
                      .tz("America/New_York")
                      .format("MMM-DD-YYYY")}
                  </span>
                ) : (
                  <span>
                    {moment(retreatDetail && retreatDetail.dateFrom)
                      .tz("America/New_York")
                      .format("MMM-DD") +
                      " - " +
                      moment(retreatDetail.dateTo)
                        .tz("America/New_York")
                        .format("MMM DD") +
                      ", " +
                      moment(retreatDetail && retreatDetail.dateFrom)
                        .tz("America/New_York")
                        .format("YYYY")}
                  </span>
                )}

                <h4 className="mt-3">Roster Data</h4>
                <div>
                  <p className="mt-1">
                    <strong>Confirm Status:</strong>{" "}
                    {retreatDetail &&
                    retreatDetail.confirmation &&
                    retreatDetail.confirmation[0] &&
                    retreatDetail.confirmation[0].roasterData
                      ? "Confirmed"
                      : "Not Confirmed"}
                  </p>
                  <p>
                    <strong>Confirmed By:</strong>{" "}
                    {retreatDetail &&
                    retreatDetail.confirmation &&
                    retreatDetail.confirmation[0] &&
                    retreatDetail.confirmation[0].confirmedByName
                      ? `${retreatDetail.confirmation[0].confirmedByName} (${
                          retreatDetail.confirmation[0].confirmedByEmail ||
                          "Nil"
                        })`
                      : "Nil"}
                  </p>
                  <p>
                    <strong>Role:</strong>{" "}
                    {retreatDetail &&
                    retreatDetail.confirmation &&
                    retreatDetail.confirmation[0] &&
                    retreatDetail.confirmation[0].confirmedByRole
                      ? retreatDetail.confirmation[0].confirmedByRole
                      : "Nil"}
                  </p>
                  <p>
                    <strong>Confirmed Date:</strong>{" "}
                    {retreatDetail &&
                    retreatDetail.confirmation &&
                    retreatDetail.confirmation[0] &&
                    retreatDetail.confirmation[0].ConfirmedDate
                      ? Helpers.convertUtcToTimezone(
                          retreatDetail.confirmation[0].ConfirmedDate,
                          "MM-DD-YYYY hh:mm:a",
                        )
                      : "Nil"}
                  </p>
                </div>
                <hr className="mt-3" style={{ border: "1px solid #ccc" }} />
                <h4 className="mt-3">Directory</h4>
                <div>
                  <p className="mt-1">
                    <strong>Confirm Status: </strong>{" "}
                    {retreatDetail &&
                    retreatDetail.confirmation &&
                    retreatDetail.confirmation[1] &&
                    retreatDetail.confirmation[1].Directory
                      ? "Confirmed"
                      : "Not Confirmed"}
                  </p>
                  <p>
                    <strong>Confirmed By: </strong>{" "}
                    {retreatDetail &&
                    retreatDetail.confirmation &&
                    retreatDetail.confirmation[1] &&
                    retreatDetail.confirmation[1].confirmedByName
                      ? `${retreatDetail.confirmation[1].confirmedByName} (${
                          retreatDetail.confirmation[1].confirmedByEmail ||
                          "Nil"
                        })`
                      : "Nil"}
                  </p>
                  <p>
                    <strong>Role: </strong>{" "}
                    {retreatDetail &&
                    retreatDetail.confirmation &&
                    retreatDetail.confirmation[1] &&
                    retreatDetail.confirmation[1].confirmedByRole
                      ? retreatDetail.confirmation[1].confirmedByRole
                      : "Nil"}
                  </p>
                  <p>
                    <strong>Confirmed Date:</strong>{" "}
                    {retreatDetail &&
                    retreatDetail.confirmation &&
                    retreatDetail.confirmation[1] &&
                    retreatDetail.confirmation[1].ConfirmedDate
                      ? Helpers.convertUtcToTimezone(
                          retreatDetail.confirmation[1].ConfirmedDate,
                          "MM-DD-YYYY hh:mm:a",
                        )
                      : "Nil"}
                  </p>
                </div>
                <hr className="mt-3" style={{ border: "1px solid #ccc" }} />
                <h4>Program</h4>
                <div>
                  <p className="mt-1">
                    <strong>Confirm Status:</strong>{" "}
                    {retreatDetail &&
                    retreatDetail.confirmation &&
                    retreatDetail.confirmation[2] &&
                    retreatDetail.confirmation[2].Program
                      ? "Confirmed"
                      : "Not Confirmed"}
                  </p>
                  <p>
                    <strong>Confirmed By:</strong>{" "}
                    {retreatDetail &&
                    retreatDetail.confirmation &&
                    retreatDetail.confirmation[2] &&
                    retreatDetail.confirmation[2].confirmedByName
                      ? `${retreatDetail.confirmation[2].confirmedByName} (${
                          retreatDetail.confirmation[2].confirmedByEmail ||
                          "Nil"
                        })`
                      : "Nil"}
                  </p>
                  <p>
                    <strong>Role:</strong>{" "}
                    {retreatDetail &&
                    retreatDetail.confirmation &&
                    retreatDetail.confirmation[2] &&
                    retreatDetail.confirmation[2].confirmedByRole
                      ? retreatDetail.confirmation[2].confirmedByRole
                      : "Nil"}
                  </p>
                  <p>
                    <strong>Confirmed Date:</strong>{" "}
                    {retreatDetail &&
                    retreatDetail.confirmation &&
                    retreatDetail.confirmation[2] &&
                    retreatDetail.confirmation[2].ConfirmedDate
                      ? Helpers.convertUtcToTimezone(
                          retreatDetail.confirmation[2].ConfirmedDate,
                          "MM-DD-YYYY hh:mm:a",
                        )
                      : "Nil"}
                  </p>
                </div>
                <hr className="mt-3" style={{ border: "1px solid #ccc" }} />
                <h4>Retreat Status</h4>
                <div>
                  <p className="mt-1">
                    <strong>Status:</strong>{" "}
                    {retreatDetail &&
                    retreatDetail.confirmation &&
                    retreatDetail.confirmation[4] &&
                    retreatDetail.confirmation[4].retreatStatus === "ACTIVE"
                      ? "ACTIVE"
                      : "INACTIVE"}
                  </p>
                  <p>
                    <strong>By:</strong>{" "}
                    {retreatDetail &&
                    retreatDetail.confirmation &&
                    retreatDetail.confirmation[4] &&
                    retreatDetail.confirmation[4].confirmedByName
                      ? `${retreatDetail.confirmation[4].confirmedByName} (${
                          retreatDetail.confirmation[4].confirmedByEmail ||
                          "Nil"
                        })`
                      : "Nil"}
                  </p>
                  <p>
                    <strong>Role:</strong>{" "}
                    {retreatDetail &&
                    retreatDetail.confirmation &&
                    retreatDetail.confirmation[4] &&
                    retreatDetail.confirmation[4].confirmedByRole
                      ? retreatDetail.confirmation[4].confirmedByRole
                      : "Nil"}
                  </p>
                  <p>
                    <strong>Date:</strong>{" "}
                    {retreatDetail &&
                    retreatDetail.confirmation &&
                    retreatDetail.confirmation[4] &&
                    retreatDetail.confirmation[4].ConfirmedDate
                      ? Helpers.convertUtcToTimezone(
                          retreatDetail.confirmation[4].ConfirmedDate,
                          "MM-DD-YYYY hh:mm:a",
                        )
                      : "Nil"}
                  </p>
                </div>
                <hr className="mt-3" style={{ border: "1px solid #ccc" }} />

                <h4>Retreat Pictures</h4>
                <div>
                  <p className="mt-1">
                    <strong>Confirm Status:</strong>{" "}
                    {retreatDetail &&
                    retreatDetail.confirmation &&
                    retreatDetail.confirmation[3] &&
                    retreatDetail.confirmation[3].pictures
                      ? "Confirmed"
                      : "Not Confirmed"}
                  </p>
                  <p>
                    <strong>Confirmed By:</strong>{" "}
                    {retreatDetail &&
                    retreatDetail.confirmation &&
                    retreatDetail.confirmation[3] &&
                    retreatDetail.confirmation[3].confirmedByName
                      ? `${retreatDetail.confirmation[3].confirmedByName} (${
                          retreatDetail.confirmation[3].confirmedByEmail ||
                          "Nil"
                        })`
                      : "Nil"}
                  </p>
                  <p>
                    <strong>Role:</strong>{" "}
                    {retreatDetail &&
                    retreatDetail.confirmation &&
                    retreatDetail.confirmation[3] &&
                    retreatDetail.confirmation[3].confirmedByRole
                      ? retreatDetail.confirmation[3].confirmedByRole
                      : "Nil"}
                  </p>
                  <p>
                    <strong>Confirmed Date:</strong>{" "}
                    {retreatDetail &&
                    retreatDetail.confirmation &&
                    retreatDetail.confirmation[3] &&
                    retreatDetail.confirmation[3].ConfirmedDate
                      ? Helpers.convertUtcToTimezone(
                          retreatDetail.confirmation[3].ConfirmedDate,
                          "MM-DD-YYYY hh:mm:a",
                        )
                      : "Nil"}
                  </p>
                </div>
              </div>
            ) : (
              <div>No confirmation data available</div>
            )
          }
          handleClose={handleCloseAlert}
          buttonText={"Close"}
        />
      )}
      {leadMail && (
        <MailRecipientsModal
          show={leadMail}
          handleClose={handleCloseMail}
          retreatDetail={retreatDetail}
          fetchRecipients={fetchRecipients}
          initialMessage={mailMessage}
          actionFunction={(selectedIds, content, additionalEmails) => {
            setMailMessage(content);
            sendMailToLead(selectedIds, content, additionalEmails);
          }}
          heading="Send Email to Retreat Adminstrators and Steering Committee"
          cancel="Cancel"
          okay="SEND"
        />
      )}
    </div>
  );
};

export default ClosuresList;
